import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import i18n from '../../i18n';
import './Tournaments.css';

const SIDE_COLORS = {
  winners: 'var(--accent-green)',
  losers: 'var(--accent-red)',
  grand_final: 'var(--accent-orange)',
};

/**
 * Formats a remaining-seconds value into a human-readable countdown string.
 * @param {number} seconds - Remaining seconds until the scheduled time.
 * @returns {string} Formatted countdown (e.g. "2t 15m", "45m", "30s").
 */
function formatCountdown(seconds) {
  if (seconds <= 0) return i18n.t('tournament.countdownNow');
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return i18n.t('tournament.countdownHM', { h, m });
  if (m > 0) return i18n.t('tournament.countdownMS', { m, s });
  return i18n.t('tournament.countdownS', { s });
}

/**
 * Single match card in the bracket with optional countdown timer.
 * @param {object} props
 * @param {object} props.match - Match data from the API.
 */
function BracketMatchCard({ match }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    if (!match.scheduled_at || match.status === 'completed' || match.status === 'live' || match.status === 'bye') {
      setCountdown(null);
      return;
    }
    const target = new Date(match.scheduled_at).getTime();

    const tick = () => {
      const remaining = Math.max(0, Math.floor((target - Date.now()) / 1000));
      setCountdown(remaining);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [match.scheduled_at, match.status]);

  const statusClass = match.status === 'live' ? 'live'
    : match.status === 'completed' ? 'completed'
    : match.status === 'ready' ? 'ready'
    : match.status === 'bye' ? 'bye'
    : '';

  const isWinner1 = match.winner_registration_id === match.registration1_id;
  const isWinner2 = match.winner_registration_id === match.registration2_id;

  return (
    <div
      className={`bracket-match-card ${statusClass} ${match.match_id ? 'clickable' : ''}`}
      onClick={() => match.match_id && navigate(`/stats/matches/${match.match_id}`)}
      title={match.match_id ? t('tournament.matchTitle', { id: match.match_id }) : ''}
    >
      {countdown !== null && countdown > 0 && (
        <div className={`bracket-match-countdown ${countdown < 300 ? 'urgent' : 'normal'}`}>
          {formatCountdown(countdown)}
        </div>
      )}
      {countdown === 0 && match.status !== 'completed' && (
        <div className="bracket-match-countdown ready">
          {t('tournament.readyToStart')}
        </div>
      )}
      <div className="bracket-match-team-row">
        <span className={`bracket-match-team-name ${isWinner1 ? 'winner' : ''} ${!match.reg1_name ? 'tbd' : ''}`}>
          {match.reg1_name || 'TBD'}
        </span>
        {match.score1 != null && <span className={`bracket-match-score ${isWinner1 ? 'winner' : ''}`}>{match.score1}</span>}
        {match.status === 'live' && <span className="bracket-match-live-badge">LIVE</span>}
      </div>
      <div className="bracket-match-separator" />
      <div className="bracket-match-team-row">
        <span className={`bracket-match-team-name ${isWinner2 ? 'winner' : ''} ${!match.reg2_name ? 'tbd' : ''}`}>
          {match.reg2_name || 'TBD'}
        </span>
        {match.score2 != null && <span className={`bracket-match-score ${isWinner2 ? 'winner' : ''}`}>{match.score2}</span>}
        {match.status === 'bye' && <span className="bracket-match-bye">BYE</span>}
      </div>
    </div>
  );
}

/**
 * Render a single round column with vertically centered matches.
 * @param {object} props
 * @param {object} props.round - Round object from API.
 * @param {boolean} props.hasConnectors - Whether to render left connectors.
 * @param {boolean} props.hasRightConnectors - Whether to render right connectors.
 */
function BracketRound({ round, hasConnectors, hasRightConnectors }) {
  return (
    <div className="bracket-column">
      <div className="bracket-column-header">{round.name}</div>
      <div className="bracket-column-matches">
        {round.matches.map((m) => (
          <div
            key={m.id}
            className={`bracket-slot${hasConnectors ? ' has-connector-left' : ''}${hasRightConnectors ? ' has-connector-right' : ''}`}
          >
            <BracketMatchCard match={m} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Render a horizontal pyramid bracket section with connector lines.
 * Each round is a column; matches are vertically spaced to form the pyramid.
 * CSS handles the connector lines via pseudo-elements.
 * @param {object} props
 * @param {Array} props.rounds - Array of round objects with matches.
 * @param {string} [props.label] - Section label (e.g. "Winners Bracket").
 * @param {string} [props.color] - Accent color for the section label.
 */
function BracketSection({ rounds, label, color }) {
  if (!rounds || rounds.length === 0) return null;

  return (
    <div className="bracket-section">
      {label && (
        <h3
          className="bracket-section-label"
          style={{ color: color || 'var(--text-secondary)', borderBottomColor: color || 'var(--border-color)' }}
        >
          {label}
        </h3>
      )}
      <div className="bracket-pyramid">
        {rounds.map((round, idx) => (
          <BracketRound
            key={round.id}
            round={round}
            hasConnectors={idx > 0}
            hasRightConnectors={idx < rounds.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Interactive bracket visualization for single and double elimination.
 * Renders a horizontal pyramid layout with connector lines between rounds.
 * Supports WebSocket live updates.
 * @param {object} props
 * @param {string} props.tournamentId - Tournament ID for fetching bracket data.
 * @param {Array} [props.initialBracket] - Pre-loaded bracket data.
 */
export default function TournamentBracket({ tournamentId, initialBracket }) {
  const { t } = useTranslation();
  const [bracket, setBracket] = useState(initialBracket || []);
  const wsRef = useRef(null);

  useEffect(() => {
    if (initialBracket) {
      setBracket(initialBracket);
      return;
    }
    fetch(`${API_URL}/api/tournaments/${tournamentId}/bracket`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setBracket)
      .catch(() => {});
  }, [tournamentId, initialBracket]);

  useEffect(() => {
    if (!tournamentId) return;
    const wsUrl = `${API_URL.replace(/^http/, 'ws')}/api/tournaments/ws/${tournamentId}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.bracket) {
          setBracket(data.bracket);
        }
      } catch (e) {
        // ignore non-JSON messages
      }
    };

    ws.onerror = () => {};
    ws.onclose = () => {};

    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [tournamentId]);

  if (!bracket || bracket.length === 0) {
    return <div className="bracket-empty">{t('tournament.bracketNotGenerated')}</div>;
  }

  const winnersRounds = bracket.filter((r) => r.bracket_side === 'winners');
  const losersRounds = bracket.filter((r) => r.bracket_side === 'losers');
  const grandFinal = bracket.filter((r) => r.bracket_side === 'grand_final');
  const isDoubleElim = losersRounds.length > 0;

  if (!isDoubleElim) {
    return <BracketSection rounds={bracket} />;
  }

  return (
    <div>
      <BracketSection rounds={winnersRounds} label="Winners Bracket" color={SIDE_COLORS.winners} />
      <BracketSection rounds={losersRounds} label="Losers Bracket" color={SIDE_COLORS.losers} />
      <BracketSection rounds={grandFinal} label="Grand Final" color={SIDE_COLORS.grand_final} />
    </div>
  );
}
